import axios from "axios";

const API_BASE = process.env.REACT_APP_NOT_SECRET_CODE;

const ORDER_STATUS_LABELS = {
  1: "Submitted",
  2: "Processing",
  3: "Dispatched",
  4: "Completed",
  5: "Cancelled",
  6: "Attempted",
  7: "Unreachable",
  8: "Payment Pending",
  9: "Quote Sent",
  10: "Contacted",
  11: "Packed",
  12: "Ready",
  13: "Draft",
  14: "Item Pending",
  16: "Partially Delivered",
  17: "Procurement Ready",
  18: "Hold",
  19: "Order On Move",
  20: "Picked Up",
};

const getOrderStatusLabel = (status) => ORDER_STATUS_LABELS[Number(status)] || "";

const withOrderStatusLabel = (order) => {
  if (!order || order.order_status_label) {
    return order;
  }

  const status = order.status || order.order_status;
  const orderStatusLabel = getOrderStatusLabel(status);

  return orderStatusLabel
    ? { ...order, order_status_label: orderStatusLabel }
    : order;
};
const API_V2_BASE = String(API_BASE || "").replace(/\/v1\/?$/, "/v2");
const USE_ORDER_V2 = process.env.REACT_APP_USE_ORDER_V2 === "true";

const defaultHeaders = (token) => ({
  "Content-Type": "application/json",
  "X-Request-ID": "124",
  "X-Country-Code": "BD",
  "X-Client-Name": "WEBSITE",
  "X-Client-Version": "123",
  Authorization: `Bearer ${token}`,
});

const appendParam = (params, key, value) => {
  if (value === undefined || value === null || value === "") {
    return;
  }

  params.append(key, value);
};

const buildOrderParams = (filters) => {
  const params = new URLSearchParams();

  appendParam(params, "name", filters.subscriber);
  appendParam(params, "status", filters.status);
  appendParam(params, "source", filters.source);
  appendParam(params, "rider_id", filters.riderId);
  appendParam(params, "order_date", filters.orderDate);
  appendParam(params, "payment_type", filters.paymentType);
  appendParam(params, "b2b", filters.b2bCompany);
  appendParam(params, "channel", filters.channel);
  appendParam(params, "order_id", filters.orderId);
  appendParam(params, "po_number", filters.poNumber);
  appendParam(params, "from_date", filters.fromDate);
  appendParam(params, "to_date", filters.toDate);
  appendParam(params, "page", filters.page);
  appendParam(params, "records_per_page", filters.recordsPerPage);
  appendParam(params, "city_id", filters.district);
  appendParam(params, "order_type", filters.orderType);
  appendParam(params, "is_courier_partner", filters.isCourier ? 1 : "");
  appendParam(params, "order_sub_type", filters.orderSubType);
  appendParam(params, "partial_sent", filters.isPartial ? true : "");
  appendParam(params, "is_claimable", filters.isClaimable ? 1 : "");
  appendParam(params, "store_id", filters.branch);
  appendParam(params, "delivery_type", filters.deliveryType);
  appendParam(params, "total_cost_from", filters.totalCostFrom);
  appendParam(params, "total_cost_to", filters.totalCostTo);

  return params;
};

export const fetchMedicineOrders = (token, filters) => {
  const params = buildOrderParams(filters);
  const endpoint = USE_ORDER_V2 ? "/v2/orders" : "/orders";

  return axios
    .get(`${API_BASE}${endpoint}?${params.toString()}`, {
      headers: defaultHeaders(token),
    })
    .then((res) => res.data);
};

export const fetchOperationalDashboard = (token, options = {}) => {
  const params = new URLSearchParams();
  appendParam(params, "queue_limit", options.queueLimit);

  return axios
    .get(`${API_V2_BASE}/dashboard/operations${params.toString() ? `?${params.toString()}` : ""}`, {
      headers: defaultHeaders(token),
    })
    .then((res) => res.data);
};

export const fetchMedicineOrderById = (token, orderId) => {
  const endpoint = USE_ORDER_V2 ? `/v2/orders/${orderId}` : `/orders/${orderId}`;
  const legacyEndpoint = `/orders/${orderId}`;

  return axios
    .get(`${API_BASE}${endpoint}`, {
      headers: defaultHeaders(token),
    })
    .then((res) => res.data)
    .catch((error) => {
      if (!USE_ORDER_V2 || endpoint === legacyEndpoint) {
        throw error;
      }

      return axios
        .get(`${API_BASE}${legacyEndpoint}`, {
          headers: defaultHeaders(token),
        })
        .then((res) => res.data);
    });
};

const safelyFetch = (request, fallback) =>
  request()
    .then((res) => res.data)
    .catch(() => fallback);

const getPayloadRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
};

export const fetchMedicineOrderFilterOptions = (token, countryCode = "BD") =>
  Promise.all([
    safelyFetch(
      () =>
        axios.get(`${API_BASE}/b2bListWithoutPagination`, {
          headers: defaultHeaders(token),
        }),
      [],
    ),
    safelyFetch(
      () =>
        axios.get(`${API_BASE}/riders/5?district=`, {
          headers: defaultHeaders(token),
        }),
      [],
    ),
    safelyFetch(
      () =>
        axios.get(`${API_BASE}/pharmacies?country=${countryCode || "BD"}`, {
          headers: defaultHeaders(token),
        }),
      [],
    ),
  ]).then(([b2bPayload, riderPayload, branchPayload]) => ({
    b2bCompanies: getPayloadRows(b2bPayload)
      .filter((item) => item && item.company_name)
      .map((item) => ({
        value: item.company_name,
        label: item.company_name,
      })),
    riders: getPayloadRows(riderPayload)
      .filter((item) => item && item.is_active === 1)
      .map((item) => ({
        value: item.id,
        label: item.name || item.username || `Rider ${item.id}`,
      })),
    branches: getPayloadRows(branchPayload).map((item) => ({
      value: item.id,
      label: item.name || item.pharmacy_name || `Branch ${item.id}`,
    })),
  }));

export const updateMedicineOrderStatuses = (
  token,
  orders,
  cancelReason = "",
  cancelMessage = "",
) =>
  axios
    .put(
      `${API_BASE}/orders/update/status`,
      {
        orders: orders.map((order) => withOrderStatusLabel(order)),
        cancel_reason: cancelReason,
        cancel_message: cancelMessage,
      },
      {
        headers: defaultHeaders(token),
      },
    )
    .then((res) => res.data);

export const updateMedicineOrderClassic = (token, orderId, payload) =>
  axios
    .put(`${API_BASE}/orders/${orderId}`, payload, {
      headers: defaultHeaders(token),
    })
    .then((res) => res.data);

export const downloadMedicineOrderInvoice = (token, orderId, invoiceMode = "") => {
  const params = new URLSearchParams();
  appendParam(params, "invoice_mode", invoiceMode);

  return axios.get(`${API_BASE}/orders/improved-pdf/${orderId}${params.toString() ? `?${params.toString()}` : ""}`, {
    headers: defaultHeaders(token),
  });
};

export const markMedicineOrderReviewed = (token, orderId) =>
  axios.put(
    `${API_BASE}/orders/adminreview/${orderId}`,
    { review: "1" },
    {
      headers: defaultHeaders(token),
    },
  );
